import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPredicate, TComparison } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import { FormElement, SegmentedControl, UniversalCreatableSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import * as styles from './URLVisitGoal.css';

function URLVisitGoal(): JSX.Element {
  const { t } = useTranslation();
  const { Breadcrumbs } = useStore();
  const [matchMode, setMatchMode] = useState<string>('eq');
  const [selectedUrl, setSelectedUrl] = useState<IUniversalSelectOption | undefined>(undefined);
  const [predicate, setPredicate] = useState<Partial<IPredicate>>({
    attr_against: 'loc_url',
    comparison: 'starts_with',
    value: '',
  });

  const {
    spacing: { '2': gap },
  } = useMantineTheme();

  const data = [
    {
      label: t('Exact match'),
      value: 'eq',
    },
    {
      label: t('Starts with'),
      value: 'starts_with',
    },
  ];

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: readonly IUniversalSelectOption[]) => void): void => {
      const path = 'breadcrumbs/loc_url/values';
      Breadcrumbs.getSuggestions(path, inputValue)
        .then((suggestions) => {
          const options: IUniversalSelectOption[] = suggestions
            .filter((suggestion) => suggestion.value)
            .map(({ value }) => ({
              id: value,
              name: value,
            }));
          callback(options);
        })
        .catch(() => {
          callback([]);
        });
    },
    [Breadcrumbs],
  );

  function createOption(inputValue: string): IUniversalSelectOption {
    return {
      id: inputValue,
      name: inputValue,
    };
  }

  function handleChangeValue(value?: IUniversalSelectOption): void {
    setSelectedUrl(value);
    setPredicate({
      ...predicate,
      value: value?.id,
    });
  }

  function handleChangeMatchMode(value: string): void {
    setMatchMode(value);
    setPredicate({
      ...predicate,
      comparison: value as TComparison,
    });
  }

  return (
    <Flex align={'flex-end'} gap={gap}>
      <FormElement label={t('Target URL')}>
        <SegmentedControl data={data} onChange={handleChangeMatchMode} value={matchMode} />
      </FormElement>
      <UniversalCreatableSelect
        async={true}
        controlPrefix={getIconForAction('search')}
        createOption={createOption}
        defaultOptions={true}
        loadOptions={loadOptions}
        onChange={handleChangeValue}
        placeholder={t('Start typing to search URLs')}
        value={selectedUrl}
        wrapperClassName={styles.input}
      />
    </Flex>
  );
}

export default observer(URLVisitGoal);
